import * as React from 'react';

import { Button, Typography2 } from '@coursera/cds-core';

import type { NavigationElement } from 'bundles/about/__generated__/baseContentfulTypes';
import { renderLink } from 'bundles/about/utils/AboutUtils';
import { CourseraMetatags } from 'bundles/seo';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/about';

type Props = {
  // Only used for tests to pass in shorter duration.
  attemptIntervalDurationMs?: number;
};

const MAX_ATTEMPTS_TO_OPEN_MODAL = 20;
const DEFAULT_ATTEMPT_INTERVAL_DURATION_MS = 500;
const cookiesPolicyPageLink = {
  label: 'Back to Cookies Policy',
  route: '/about/cookies',
} as NavigationElement;

declare global {
  interface Window {
    OneTrust?: {
      // This doesn't actually toggle the display, just opens the cookie manager modal.
      ToggleInfoDisplay: () => void;
    };
  }
}

// The goal of this page is to open the "Cookie Manage" modal from the OneTrust script added to every page.
// In order to combat any race conditions, attempt to open the cookie modal a set number of times after page load.
// If we reach the max number of attempts we'll show an error message to the user.
const tryToOpenCookieModalAutomatically = ({
  hasOpenedModal,
  numAttemptsToOpenModal,
  setHasOpenedModal,
  setNumAttemptsToOpenModal,
  attemptIntervalDurationMs,
}: {
  hasOpenedModal: boolean;
  setHasOpenedModal: (arg0: boolean) => void;
  numAttemptsToOpenModal: number;
  setNumAttemptsToOpenModal: (arg0: number) => void;
  attemptIntervalDurationMs: number;
}) => {
  if (inServerContext || hasOpenedModal || numAttemptsToOpenModal >= MAX_ATTEMPTS_TO_OPEN_MODAL) return;

  if (window.OneTrust?.ToggleInfoDisplay) {
    // eslint-disable-next-line new-cap
    window.OneTrust.ToggleInfoDisplay();
    setHasOpenedModal(true);
  } else {
    const newAttemptNum = numAttemptsToOpenModal + 1;

    setNumAttemptsToOpenModal(newAttemptNum);

    setTimeout(() => {
      tryToOpenCookieModalAutomatically({
        hasOpenedModal: false,
        numAttemptsToOpenModal: newAttemptNum,
        setHasOpenedModal,
        setNumAttemptsToOpenModal,
        attemptIntervalDurationMs,
      });
    }, attemptIntervalDurationMs);
  }
};

const CookiesManagePage = ({ attemptIntervalDurationMs }: Props) => {
  const [hasOpenedModal, setHasOpenedModal] = React.useState(false);
  const [numAttemptsToOpenModal, setNumAttemptsToOpenModal] = React.useState(0);

  const openCookieModalOnClick = () => {
    if (window.OneTrust?.ToggleInfoDisplay) {
      // eslint-disable-next-line new-cap
      window.OneTrust.ToggleInfoDisplay();
      setHasOpenedModal(true);
    }
  };

  React.useEffect(() => {
    tryToOpenCookieModalAutomatically({
      hasOpenedModal,
      setHasOpenedModal,
      numAttemptsToOpenModal,
      setNumAttemptsToOpenModal,
      attemptIntervalDurationMs: attemptIntervalDurationMs || DEFAULT_ATTEMPT_INTERVAL_DURATION_MS,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // We expect the modal to open automatically, the button below is used for when the user closes the modal if they want to re-open it.
  return (
    <div>
      <CourseraMetatags metaNameAndProperties={{ title: 'Manage Cookies' }} />
      <div className="bt3-row-fluid coursera-about-nav">{renderLink(cookiesPolicyPageLink)}</div>
      <Button variant="ghost" onClick={openCookieModalOnClick}>
        {_t('Manage cookie preferences')}
      </Button>
      {!hasOpenedModal && numAttemptsToOpenModal >= MAX_ATTEMPTS_TO_OPEN_MODAL && (
        <Typography2 component="p">
          {_t(
            'Unable to load cookie preference popup. Please reload the page and/or disable any ad blockers to try again.'
          )}
        </Typography2>
      )}
    </div>
  );
};

export default CookiesManagePage;
